import SinglePageForm, {
    getPasswordRules,
    getConfirmPasswordRules,
} from '../common/SinglePageForm';
import React, { useContext, useEffect, useState } from 'react';
import { Input, Button, Alert, Checkbox, Form } from 'antd';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { parse as parseQueryString } from 'query-string';
import { reset } from '../services/user';
import { APP_CCX, CustomAppContext } from '../common/CustomAppContext';
import { useQueryParam, StringParam } from 'use-query-params';
import './ResetPasswordPage.less';
import classNames from 'classnames';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import {
    checkPasswordRequirements,
    trackCustomEvent,
} from '../helpers';

export default ResetPasswordPage;

const FORGOT_PASSWORD_MODE_RESET = 'reset';
const FORGOT_PASSWORD_MODE_SET = 'set';

/**
 * @name ResetPasswordPage
 * @desc ResetPasswordPage component.
 */
function ResetPasswordPage(props) {
    const [form] = Form.useForm();
    const history = useHistory();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [passwordError, setPasswordError] = useState([]);

    const {
        currentApp,
        getCustomAppButtonStyle,
        getCustomAppPath,
        getCustomAppUrl,
    } = useContext(CustomAppContext);

    const [mode] = useQueryParam('mode', StringParam);

    const validatePasswordRequirements = (password) => {
        const { isValid, errors } = checkPasswordRequirements(password);
        if (!isValid) {
            setPasswordError(errors);
            return false;
        }
        return true;
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const { t, uid } = parseQueryString(location.search);
            const { password, termsAccepted } = values;
            await reset({
                id: uid,
                token: t,
                password,
                autoLogin: mode === FORGOT_PASSWORD_MODE_SET,
                termsAccepted:
                    mode === FORGOT_PASSWORD_MODE_SET
                        ? termsAccepted
                        : undefined,
            });
            if (mode === FORGOT_PASSWORD_MODE_SET) {
                // user is setting password for first time
                window.location.assign(`${getCustomAppUrl()}/servers/install`);
            } else {
                //handling only for CCX until BN flow changes as well
                if (currentApp === APP_CCX) {
                    setSubmitted(true);
                } else {
                    history.push(getCustomAppPath('/'));
                }
            }
            trackCustomEvent(currentApp, 'reset_password_success', 'click');
        } catch (err) {
            setError(err.message);
            trackCustomEvent(currentApp, 'reset_password_failed', 'click');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmitFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
        trackCustomEvent(currentApp, 'reset_password_error', 'click');
        setError('Please check your input and try again.');
    };

    return submitted ? (
        <div className="RegisterPage-submitted-panel">
            <h1>Password reset successfully</h1>
            <Button
                data-testid="reset-password-login-button-submit"
                type="primary"
                htmlType="button"
                style={getCustomAppButtonStyle()}
                onClick={() => history.push(getCustomAppPath('/'))}
            >
                Log in to your account
            </Button>
        </div>
    ) : (
        <SinglePageForm
            form={form}
            onFinish={handleSubmit}
            onFinishFailed={handleSubmitFailed}
            data-testid="forgot-password-form"
        >
            <SinglePageForm.Content>
                <h1 className="RegisterPage-form-title">
                    {mode === FORGOT_PASSWORD_MODE_SET
                        ? 'Set Password'
                        : 'Reset Password'}
                </h1>
                <Form.Item
                    label="Password"
                    name="password"
                    rules={getPasswordRules(true)}
                    validateTrigger="onBlur"
                >
                    <Input.Password
                        data-testid="reset-password-form-input-password"
                        disabled={loading}
                        placeholder="Enter new password"
                        onChange={(e) => {
                            validatePasswordRequirements(e.target.value);
                        }}
                    />
                </Form.Item>

                <Form.Item
                    noStyle
                    shouldUpdate={(prevValues, currentValues) =>
                        prevValues.password !== currentValues.password
                    }
                >
                    {({ getFieldError }) =>
                        getFieldError('password')?.length > 0 ? (
                            <Form.Item>
                                <div
                                    className={`RegisterPage-validation-${currentApp}`}
                                >
                                    <p className="RegisterPage-validation-heading">
                                        A valid password must contain at least:
                                    </p>
                                    <div
                                        className={
                                            'RegisterPage-validation-box'
                                        }
                                    >
                                        {passwordError.map((check, index) => (
                                            <span
                                                className={classNames(
                                                    'RegisterPage-validation-points',
                                                    {
                                                        success: check.valid,
                                                        error: !check.valid,
                                                    },
                                                )}
                                                key={index}
                                            >
                                                {check.valid ? (
                                                    <CheckCircleOutlined
                                                        onPointerEnterCapture={
                                                            undefined
                                                        }
                                                        onPointerLeaveCapture={
                                                            undefined
                                                        }
                                                    />
                                                ) : (
                                                    <CloseCircleOutlined
                                                        onPointerEnterCapture={
                                                            undefined
                                                        }
                                                        onPointerLeaveCapture={
                                                            undefined
                                                        }
                                                    />
                                                )}{' '}
                                                {check.message}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </Form.Item>
                        ) : null
                    }
                </Form.Item>

                <Form.Item
                    label="Confirm Password"
                    name="confirm"
                    rules={getConfirmPasswordRules()}
                    validateTrigger="onBlur"
                >
                    <Input.Password
                        data-testid="reset-password-form-input-confirm"
                        disabled={loading}
                        placeholder="Enter your new password again"
                    />
                </Form.Item>

                {mode === FORGOT_PASSWORD_MODE_SET ? (
                    <Form.Item
                        name="termsAccepted"
                        valuePropName="checked"
                        initialValue={false}
                        rules={[
                            {
                                required: true,
                                transform: (value) => value || undefined,
                                type: 'boolean',
                                message: 'Please accept Terms and Conditions.',
                            },
                        ]}
                    >
                        <Checkbox data-testid="reset-password-form-input-terms-and-conditions">
                            I accept the{' '}
                            <Link
                                to={{
                                    pathname: '/terms-and-conditions',
                                    search: `?origin=exoscale`,
                                }}
                                target="_blank"
                            >
                                Terms and Conditions
                            </Link>{' '}
                            and{' '}
                            <Link
                                to={{
                                    pathname: '/privacy-policy',
                                    search: `?origin=exoscale`,
                                }}
                                target="_blank"
                            >
                                Privacy Policy
                            </Link>
                        </Checkbox>
                    </Form.Item>
                ) : null}

                <SinglePageForm.Footer>
                    {error ? <Alert message={error} type="error" /> : null}
                    <br />
                    <Button
                        data-testid="reset-password-form-button-submit"
                        loading={loading}
                        type="primary"
                        htmlType="submit"
                        style={{
                            ...getCustomAppButtonStyle(),
                            float: 'right' as const,
                        }}
                    >
                        Reset
                    </Button>
                </SinglePageForm.Footer>
            </SinglePageForm.Content>
        </SinglePageForm>
    );
}
